import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Spinner, Tabs, Tab } from 'react-bootstrap';
import { useOobSession } from '../contexts/OobSessionContext';
import apiClient from '../services/apiClient';

interface TokenValidation {
  accessTokenGuid: string;
  eventGuid: string;
  participantGuid: string;
  participantDisplayName: string;
  participantEmail: string;
  publicEventReference: string;
  eventName: string;
  expiresAt: string;
}

export const OobExternalEntry: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSession } = useOobSession();

  const [activeTab, setActiveTab] = useState<'enter-token' | 'request-fresh'>('enter-token');
  const [step, setStep] = useState<'token' | 'otp'>('token');
  const [token, setToken] = useState(searchParams.get('t') || '');
  const [otp, setOtp] = useState('');
  const [freshEmail, setFreshEmail] = useState('');
  const [tokenValidation, setTokenValidation] = useState<TokenValidation | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleValidateToken = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiClient.post<TokenValidation>('/api/oob/external/validate-token', { token });
      setTokenValidation(response.data);

      // Automatically send OTP after token validation
      await apiClient.post('/api/oob/external/send-otp', { 
        accessTokenGuid: response.data.accessTokenGuid 
      });

      setStep('otp');
    } catch (err: any) {
      // Extract error message from axios response or fall back to generic
      const errorMsg = err?.response?.data?.error || (err instanceof Error ? err.message : 'Failed to validate access link');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!tokenValidation) {
        throw new Error('Token validation missing');
      }

      interface VerifyOtpResponse {
        sessionToken: string;
        eventGuid: string;
        participantGuid: string;
        sessionExpiresAt: string;
      }

      const response = await apiClient.post<VerifyOtpResponse>('/api/oob/external/verify-otp', {
        accessTokenGuid: tokenValidation.accessTokenGuid,
        otp,
      });

      setSession({
        sessionToken: response.data.sessionToken,
        eventGuid: response.data.eventGuid,
        participantGuid: response.data.participantGuid,
        sessionExpiresAt: new Date(response.data.sessionExpiresAt),
      });

      // Redirect to event page
      navigate(`/oob/${response.data.eventGuid}`);
    } catch (err: any) {
      // Extract error message from axios response or fall back to generic
      const errorMsg = err?.response?.data?.error || (err instanceof Error ? err.message : 'Failed to verify code');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestFreshToken = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await apiClient.post<{ message: string }>(
        '/api/oob/external/request-fresh-token',
        { participantEmail: freshEmail }
      );

      setSuccess(response.data.message);
      setFreshEmail('');
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || (err instanceof Error ? err.message : 'Failed to request token');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-5" style={{ maxWidth: '600px' }}>
      <Card className="shadow-sm">
        <Card.Header className="bg-primary text-white">
          <Card.Title className="mb-0">Emergency Access Portal</Card.Title>
        </Card.Header>
        <Card.Body className="p-4">
          <Tabs activeKey={activeTab} onSelect={(k) => {
            setActiveTab(k as 'enter-token' | 'request-fresh');
            setError('');
            setSuccess('');
          }} className="mb-4">
            <Tab eventKey="enter-token" title="Enter Token">
              {step === 'token' ? (
                <>
                  <p className="text-muted mb-4">
                    Enter your emergency access link or token to proceed.
                  </p>

                  {error && <Alert variant="danger">{error}</Alert>}
                  {success && <Alert variant="success">{success}</Alert>}

                  <Form onSubmit={handleValidateToken}>
                    <Form.Group className="mb-3">
                      <Form.Label>Access Link or Token</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Paste the access token from your email or SMS"
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        disabled={loading}
                      />
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      disabled={!token.trim() || loading}
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Validating...
                        </>
                      ) : (
                        'Continue'
                      )}
                    </Button>
                  </Form>
                </>
              ) : (
                <>
                  <div className="mb-4 p-3 bg-light border rounded">
                    <p className="mb-2">
                      <strong>Event:</strong> {tokenValidation?.eventName}
                    </p>
                    <p className="mb-0">
                      <strong>Reference:</strong> {tokenValidation?.publicEventReference}
                    </p>
                  </div>

                  <p className="text-muted mb-4">
                    A verification code was sent to {tokenValidation?.participantEmail}. Enter it below to continue.
                  </p>

                  {error && <Alert variant="danger">{error}</Alert>}

                  <Form onSubmit={handleVerifyOtp}>
                    <Form.Group className="mb-3">
                      <Form.Label>Verification Code</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="000000"
                        maxLength={6}
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                        disabled={loading}
                        autoComplete="off"
                      />
                      <Form.Text className="text-muted">
                        Enter the 6-digit code from your email or text message
                      </Form.Text>
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      disabled={otp.length !== 6 || loading}
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Verifying...
                        </>
                      ) : (
                        'Verify & Continue'
                      )}
                    </Button>

                    <Button
                      variant="link"
                      className="w-100 mt-2"
                      onClick={() => {
                        setStep('token');
                        setOtp('');
                        setError('');
                      }}
                      disabled={loading}
                    >
                      Back
                    </Button>
                  </Form>
                </>
              )}
            </Tab>
            <Tab eventKey="request-fresh" title="Request Fresh Token">
              <p className="text-muted mb-4">
                Don't have an access token, or yours expired? Enter your email and we'll send you a new one for any active events where you're invited.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <Form onSubmit={handleRequestFreshToken}>
                <Form.Group className="mb-3">
                  <Form.Label>Your Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="your.email@enmax.com"
                    value={freshEmail}
                    onChange={(e) => setFreshEmail(e.target.value)}
                    disabled={loading}
                  />
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100"
                  disabled={!freshEmail.trim() || loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Access Link'
                  )}
                </Button>
              </Form>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </Container>
  );
};
